import { useState } from 'react';

import { CountrySelect } from '../components/CountrySelect.js';
import {
  SubmitRefusalLine,
  anyFieldError,
  useSubmitRefusal,
  type FieldErrors,
} from '../components/SubmitRefusal.js';
import { ViewOnlyNotice, useWritesRefused } from '../components/SuspendedNotice.js';
import { Alert, Button, Card, TextField } from '../components/ui.js';
import { startLoginEmailChange, updateAgencySettings } from '../lib/agencyApi.js';
import { BRAND_CONTACT_EMAIL } from '../lib/brand.js';
import { EU_COUNTRIES } from '../lib/euCountries.js';
import { ApiError } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { useSession } from '../session/SessionProvider.js';
import type { SessionAgency } from '../session/types.js';

/**
 * Account settings — ruling D-1.
 *
 * Sign-up now asks for one email address and uses it for billing too. This is
 * where an agency separates the two later, adds a VAT number, or corrects the
 * country they picked in a hurry.
 *
 * Not editable here: the agency name, which is the account identity the
 * operator knows them by, and the payment customer reference, which is
 * write-once by design.
 */

interface FormState {
  /** D-14 Item 8.4. Editable from Phase B; it used to be a support ticket. */
  name: string;
  billingEmail: string;
  country: string;
  vatNumber: string;
  /** D-16 ruling 2. Editable here; required at the buy gate. */
  businessWebsite: string;
}

const FIELD_MAP: Record<string, keyof FormState> = {
  name: 'name',
  billingEmail: 'billingEmail',
  country: 'country',
  vatNumber: 'vatNumber',
  businessWebsite: 'businessWebsite',
};

export function SettingsPage(): React.JSX.Element {
  const { session, refresh } = useSession();
  const agency = session?.agency ?? null;

  /*
   * **WAIT-B round 2, defect 3.** Every control on this page was live under
   * view-as: the fields took typing and Save changes was pressable, and the
   * only thing that said otherwise was the 403 at the end of it.
   *
   * `'any-state'` because `PATCH /api/agencies/:id` and
   * `POST /api/me/login-email` carry `refuseWhenImpersonating` and **not**
   * `requireAgencyState`. A suspended agency may still correct its own billing
   * address and move its login email, so the stricter reading would grey out a
   * form the server would have accepted.
   */
  const { refused: viewOnly } = useWritesRefused('any-state');


  const [form, setForm] = useState<FormState>({
    name: agency?.name ?? '',
    billingEmail: agency?.billingEmail ?? '',
    country: agency?.country ?? '',
    vatNumber: agency?.vatNumber ?? '',
    businessWebsite: agency?.businessWebsite ?? '',
  });

  /*
   * **Read from the country being chosen, not the saved one** - WAIT-D16
   * polish 2, and the first draft had this backwards.
   *
   * The argument for the saved country was that flipping the label mid-edit
   * moves the goalposts. In practice it does the opposite: somebody picking
   * Ireland is told the number is optional, saves, and only then learns they
   * need one - so they have to come back and save twice. The label follows the
   * choice, and both facts land in the same save.
   */
  const inEu = EU_COUNTRIES.includes(form.country.toUpperCase());

  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  // WAIT-PL ruling 1: a refusal is shown at the button and moves to the field.
  const refusal = useSubmitRefusal();

  if (!agency) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <Alert tone="info">Operator accounts have no agency settings.</Alert>
      </div>
    );
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (!agency) return;

    setError(null);
    setFieldErrors({});
    setSaved(false);
    refusal.clear();
    setBusy(true);

    try {
      await updateAgencySettings(agency.id, {
        // D-14 Item 8.4. Sent every save, like the other three: the server
        // COALESCEs and only writes an activity event when it actually moved.
        name: form.name.trim(),
        billingEmail: form.billingEmail.trim(),
        country: form.country.trim(),
        // Empty clears it; the server treats "" as null.
        vatNumber: form.vatNumber.trim(),
        businessWebsite: form.businessWebsite.trim(),
      });
      await refresh();
      setSaved(true);
    } catch (caught) {
      if (caught instanceof ApiError && caught.fieldIssues.length > 0) {
        const mapped: Partial<Record<keyof FormState, string>> = {};
        for (const issue of caught.fieldIssues) {
          const field = FIELD_MAP[issue.field.split('.')[0] ?? ''];
          if (field) mapped[field] = issue.message;
        }
        setFieldErrors(mapped);
        setError('Please check the highlighted fields.');
        refusal.refuseFields();
      } else {
        const sentence =
          caught instanceof ApiError ? caught.message : 'We could not save your settings.';
        setError(sentence);
        refusal.refuse(sentence);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Your agency name, how we reach you, and the address you sign in with.
        </p>
      </div>

      {viewOnly ? <ViewOnlyNotice /> : null}

      <form
        ref={refusal.formRef}
        onSubmit={(event) => void handleSubmit(event)}
        className="space-y-6"
        noValidate
      >
        {error ? <Alert>{error}</Alert> : null}
        {saved ? <Alert tone="success">Your settings are saved.</Alert> : null}

        <Card className="space-y-5">
          <h2 className="text-sm font-semibold text-fg-muted">Your agency</h2>

          {/*
            D-14 Item 8.4. This used to be read-only with "Need your agency
            name changed? Get in touch and we will sort it out." underneath,
            which is a support ticket for a text field. The change goes on the
            activity timeline rather than into `admin_actions`: it is the
            customer's own act.
          */}
          <TextField
            label="Agency name"
            hint="What we call you on orders, invoices and emails."
            name="name"
            value={form.name}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            error={fieldErrors.name}
            disabled={viewOnly}
          />

          <h2 className="pt-2 text-sm font-semibold text-fg-muted">Billing</h2>

          <TextField
            label="Billing email address"
            hint="Where invoices, receipts and renewal reminders are sent. It started as your sign-in address."
            type="email"
            name="billingEmail"
            value={form.billingEmail}
            onChange={(event) =>
              setForm((current) => ({ ...current, billingEmail: event.target.value }))
            }
            error={fieldErrors.billingEmail}
            disabled={viewOnly}
          />

          <CountrySelect
            label="Country"
            hint="Start typing to find it."
            name="country"
            value={form.country}
            onChange={(code) => setForm((current) => ({ ...current, country: code }))}
            error={fieldErrors.country}
            disabled={viewOnly}
          />

          {/*
            **D-16 ruling 2.** Beside billing rather than in its own card: it is
            a fact about the account, and a buyer sent here by the buy gate
            should find both things it asked for on one screen.
          */}
          <TextField
            label="Business website"
            hint="Your website, or a LinkedIn or business profile if you don't have one."
            name="businessWebsite"
            autoComplete="url"
            value={form.businessWebsite}
            onChange={(event) =>
              setForm((current) => ({ ...current, businessWebsite: event.target.value }))
            }
            error={fieldErrors.businessWebsite}
            disabled={viewOnly}
          />

          {/*
            **The label changes with the country** - D-16 item 4.
            "VAT or GST number (optional)" on an Irish account would be a lie
            about whether they can buy, and "(required to buy credits)" on a US
            one would ask for something that does not exist there.
          */}
          <TextField
            label={
              inEu
                ? 'VAT number (required to buy credits)'
                : 'VAT or GST number (optional)'
            }
            hint={
              inEu
                ? "We check it on the EU's VIES register when you save."
                : 'Optional. Leave blank if you do not have one.'
            }
            name="vatNumber"
            value={form.vatNumber}
            onChange={(event) =>
              setForm((current) => ({ ...current, vatNumber: event.target.value }))
            }
            error={fieldErrors.vatNumber}
            disabled={viewOnly}
          />

          {agency.vatNumber ? <TaxStatusLine status={agency.taxIdStatus} /> : null}

          <SubmitRefusalLine message={refusal.message} />

          <Button type="submit" busy={busy} disabled={viewOnly}>
            Save changes
          </Button>
        </Card>
      </form>

      <Card title="Your account">
        <dl className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-sm text-fg-muted">Agency</dt>
            <dd className="mt-0.5 text-sm text-fg">{agency.name}</dd>
          </div>
          <div>
            <dt className="text-sm text-fg-muted">Signed in as</dt>
            <dd className="mt-0.5 text-sm text-fg">{session?.user.email}</dd>
          </div>
          <div>
            <dt className="text-sm text-fg-muted">Member since</dt>
            <dd className="mt-0.5 text-sm text-fg">{formatDate(agency.createdAt)}</dd>
          </div>
        </dl>
      </Card>

      <LoginEmailCard currentEmail={session?.user.email ?? ''} viewOnly={viewOnly} />
    </div>
  );
}

/**
 * Changing the login email - D-14 Item 8.4.
 *
 * **Its own card, and not part of the settings form**, because it is not a
 * setting: nothing changes when the button is pressed. A link goes to the new
 * address and the account moves only when it is clicked, which is a different
 * shape from "save changes" and is worth looking different.
 *
 * The address is typed twice, as the invite address is, because this is the
 * only way into an account that may have credits on it.
 *
 * **The success message deliberately does not confirm the address is free.**
 * The server answers the same way whether or not it is already in use, so that
 * this page cannot be used to find out who has an account. What it promises is
 * what it knows: if the address can be used, a link is on its way.
 *
 * **Read-only under view-as** (WAIT-B round 2). This card mattered most of the
 * four: an operator who filled it in would have been sending a link that moves
 * the address somebody else signs in with, and the only thing that stopped it
 * was the 403.
 */
function LoginEmailCard({
  currentEmail,
  viewOnly,
}: {
  currentEmail: string;
  viewOnly: boolean;
}): React.JSX.Element {
  const [newEmail, setNewEmail] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [missing, setMissing] = useState<FieldErrors<'newEmail' | 'confirm'>>({});
  const refusal = useSubmitRefusal<HTMLDivElement>();

  const mismatched =
    confirm.trim() !== '' && newEmail.trim().toLowerCase() !== confirm.trim().toLowerCase();

  /*
   * **Always enabled** - C-120, Craig's ruling of 30 September 2026. Pressed
   * with an address missing, or the two not matching, it says which under the
   * box and moves there. The mismatch keeps the message it already showed.
   */
  async function handleSubmit(): Promise<void> {
    refusal.clear();
    const nextMissing: FieldErrors<'newEmail' | 'confirm'> = {
      newEmail: newEmail.trim() === '' ? 'Add the new email address' : undefined,
      confirm: confirm.trim() === '' ? 'Type the new email address again' : undefined,
    };
    if (anyFieldError(nextMissing) || mismatched) {
      setMissing(nextMissing);
      refusal.refuseFields();
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await startLoginEmailChange({ newEmail: newEmail.trim(), newEmailConfirm: confirm.trim() });
      setNewEmail('');
      setConfirm('');
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'We could not start that change.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Login email">
      <p className="mt-3 text-sm text-fg-2">
        You sign in with <span className="font-medium text-fg">{currentEmail}</span>.
        Changing it sends a link to the new address, and nothing changes until you click it. We
        will let this address know either way.
      </p>

      <div ref={refusal.formRef} className="mt-4 space-y-4">
        {viewOnly ? <ViewOnlyNotice /> : null}
        {error ? <Alert>{error}</Alert> : null}
        {sent ? (
          <Alert tone="success">
            If that address can be used, a link is on its way to it. Your login email stays the
            same until you click it.
          </Alert>
        ) : null}

        <TextField
          label="New login email"
          type="email"
          value={newEmail}
          onChange={(event) => {
            setNewEmail(event.target.value);
            setMissing((current) => ({ ...current, newEmail: undefined }));
          }}
          error={missing.newEmail}
          disabled={viewOnly}
        />
        <TextField
          label="New login email again"
          type="email"
          hint="Typed twice, because this address is the only way into your account."
          value={confirm}
          onChange={(event) => {
            setConfirm(event.target.value);
            setMissing((current) => ({ ...current, confirm: undefined }));
          }}
          error={mismatched ? 'The two addresses do not match.' : missing.confirm}
          disabled={viewOnly}
        />

        <SubmitRefusalLine message={refusal.message} />
        <Button
          variant="secondary"
          busy={busy}
          disabled={viewOnly}
          onClick={() => void handleSubmit()}
        >
          Send the link
        </Button>
      </div>
    </Card>
  );
}


/**
 * What the VIES check said about the number on this account - D-16 item 4.
 *
 * **Says which register, and what happens next.** "Invalid" on its own reads as
 * an accusation; what the buyer needs is to know who said so and what it stops
 * them doing. `unavailable` is the one that has to be reassuring rather than
 * alarming: by ruling 4 they can still buy, and telling them otherwise would
 * generate a support message about a problem they do not have.
 */
function TaxStatusLine({
  status,
}: {
  status: SessionAgency['taxIdStatus'];
}): React.JSX.Element | null {
  if (status === 'none') return null;

  if (status === 'valid') {
    return <Alert tone="success">Checked on the EU&rsquo;s VIES register and valid.</Alert>;
  }
  if (status === 'invalid') {
    return (
      <Alert>
        This number is not showing as valid on the EU&rsquo;s VIES register. Check it and save
        again, or write to {BRAND_CONTACT_EMAIL}.
      </Alert>
    );
  }
  if (status === 'unavailable') {
    // **Amber, not the neutral grey it was** - WAIT-D16 polish 3. It means "not
    // confirmed yet", which is a thing to come back to, not a hint.
    return (
      <Alert tone="warning">
        We could not reach the VIES register to check this number. You can still buy credits; we
        will try again next time.
      </Alert>
    );
  }
  // `unchecked`: a number outside the EU, which nothing checks and which never
  // blocks anything. Saying "unchecked" would invite a question with no answer.
  return null;
}
